'use client';

import { createContext, useContext } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DEFAULT_STUDIO_REVIEW_FEATURES, type StudioReviewFeatures, isFeatureEnabled } from '@/lib/feature-flags';

const ConversationalDesignContext = createContext(false);
const AnalyticsContext = createContext(false);
const KnowledgeContext = createContext(false);
const HorizonContext = createContext(false);
const ScenesContext = createContext(false);
const StudioReviewContext = createContext(DEFAULT_STUDIO_REVIEW_FEATURES);
export function useStudioReviewFeatures() { return useContext(StudioReviewContext); }

const StudioExtensionsContext = createContext(false);
export function useStudioExtensionsEnabled() { return useContext(StudioExtensionsContext); }
/** Preview's Debug footer; implied by extensions, or enabled on its own. */
const PreviewDebugContext = createContext(false);
export function usePreviewDebugEnabled() { return useContext(PreviewDebugContext); }
export function useAnalyticsEnabled() { return useContext(AnalyticsContext); }
export function useKnowledgeAgentsEnabled() { return useContext(KnowledgeContext); }
export function useHorizonAgentsEnabled() { return useContext(HorizonContext); }
export function useScenesEnabled() { return useContext(ScenesContext); }

/** The server supplies the deployment flag; authors need access to a supported type. */
export function StudioFeaturesProvider({ conversationalDesign, analytics = false, knowledge = false, horizon = false, scenes = false, extensions = false, previewDebug = false, review = DEFAULT_STUDIO_REVIEW_FEATURES, children }: {
  conversationalDesign: boolean;
  analytics?: boolean;
  knowledge?: boolean;
  horizon?: boolean;
  scenes?: boolean;
  extensions?: boolean;
  previewDebug?: boolean;
  review?: StudioReviewFeatures;
  children: React.ReactNode;
}) {
  return <StudioReviewContext.Provider value={review}><StudioExtensionsContext.Provider value={extensions}><PreviewDebugContext.Provider value={extensions || previewDebug}><HorizonContext.Provider value={horizon}><KnowledgeContext.Provider value={knowledge}><ScenesContext.Provider value={scenes}><AnalyticsContext.Provider value={analytics}><ConversationalDesignContext.Provider value={conversationalDesign}>{children}</ConversationalDesignContext.Provider></AnalyticsContext.Provider></ScenesContext.Provider></KnowledgeContext.Provider></HorizonContext.Provider></PreviewDebugContext.Provider></StudioExtensionsContext.Provider></StudioReviewContext.Provider>;
}

export function useConversationalDesignEnabled(): boolean {
  const enabled = useContext(ConversationalDesignContext);
  const { isAuthenticated, hasAnyPermission } = useAuth();
  return enabled && isAuthenticated && (
    (isFeatureEnabled('agents') && hasAnyPermission(['AIManager:ManageAgents'])) ||
    (isFeatureEnabled('workflows') && hasAnyPermission(['AIManager:ManageAgents']))
  );
}
